import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import UsersTableHeader from "./UsersTableHeader";
import {
  useDeleteAdminMutation,
  useGetAdminsQuery,
} from "@/features/models/adminsApi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { createColumns } from "./columns";
import DataTable from "@/components/shared/DataTable";
import { selectUser } from "@/features/auth/authSlice";
import { useSelector } from "react-redux";

export default function Users() {
  const navigate = useNavigate();
  const { role } = useParams();
  const currentUser = useSelector(selectUser);
  if (currentUser.role !== "admin") {
    navigate("/dashboard");
  }

  const [deleteUser, { isLoading: isDeleting }] = useDeleteAdminMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Filter states
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const config = {
    title: role === "admin" ? "All Admins" : "All Guests",
    description:
      role === "admin" ? "Manage all system admins" : "Manage all guest users",
    badgeVariant: "outline",
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id).unwrap();
      toast.success("Admin deleted successfully");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error("Failed to delete admin");
      console.error("Delete error:", error);
    }
  };

  const handleEditClick = (user) => {
    navigate(`/dashboard/manage-users/${role}/edit/${user.id}`);
  };

  // Reset page when filters change
  const handleSearchChange = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  // Create columns with callback functions
  const columns = createColumns(handleDeleteClick, handleEditClick);

  return (
    <div className="p-6">
      <div className="justify-between flex items-center mb-4">
        <div>
          <h1 className="text-2xl font-semibold">{config.title}</h1>
          <p className="text-sm text-gray-600 mt-1">{config.description}</p>
        </div>
        <Button
          variant="outline"
          className="h-8"
          onClick={() => {
            navigate(`/dashboard/manage-users/${role}/new`);
          }}
        >
          Create {role === "user" ? "Guest" : "Admin"}
        </Button>
      </div>

      <DataTable
        columns={columns}
        queryHook={useGetAdminsQuery}
        queryParams={{ role }} // No role param
        searchValue={searchValue}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        header={
          <UsersTableHeader
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
          />
        }
        onSelectionChange={(selectedRows) => {
          // console.log("Selected admins:", selectedRows);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Admin</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the admin "{userToDelete?.email}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
